import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateFxExchangeRate,
  FxExchangeRate,
  FxRevaluationListRow,
  FxRevaluationStatus,
  FxRevaluationWithLines,
  ReverseFxRevaluation,
  ReverseFxRevaluationResult,
  RunFxRevaluation,
  RunFxRevaluationResult,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Cache-key scopes — keyed by companyId per convention (matches accruals / lva).
const ratesKey = (companyId: string | undefined) => ['fx-rates', companyId] as const;
const revaluationsKey = (companyId: string | undefined) => ['fx-revaluations', companyId] as const;

// ============================================================
// FX Rates
// ============================================================

export interface FxRateFilters {
  currency?: string;
  dateFrom?: string; // ISO date
  dateTo?: string;
}

export function useFxRates(companyId: string | undefined, filters: FxRateFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.currency) qs.set('currency', filters.currency);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...ratesKey(companyId), filters],
    queryFn: () =>
      api<FxExchangeRate[]>(`/companies/${companyId}/fx/rates${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useUpsertFxRate(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFxExchangeRate) =>
      apiJson<CreateFxExchangeRate, FxExchangeRate>(
        'POST',
        `/companies/${companyId}/fx/rates`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ratesKey(companyId) }),
  });
}

// ============================================================
// FX Revaluations
// ============================================================

export interface FxRevaluationFilters {
  period?: string;
  currency?: string;
  status?: FxRevaluationStatus;
}

export function useFxRevaluations(
  companyId: string | undefined,
  filters: FxRevaluationFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.currency) qs.set('currency', filters.currency);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...revaluationsKey(companyId), filters],
    queryFn: () =>
      api<FxRevaluationListRow[]>(
        `/companies/${companyId}/fx-revaluations${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useFxRevaluation(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['fx-revaluations', companyId, id],
    queryFn: () => api<FxRevaluationWithLines>(`/companies/${companyId}/fx-revaluations/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useRunFxRevaluation(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RunFxRevaluation) =>
      apiJson<RunFxRevaluation, RunFxRevaluationResult>(
        'POST',
        `/companies/${companyId}/fx-revaluations/run`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: revaluationsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useReverseFxRevaluation(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReverseFxRevaluation) =>
      apiJson<ReverseFxRevaluation, ReverseFxRevaluationResult>(
        'POST',
        `/companies/${companyId}/fx-revaluations/reverse`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: revaluationsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}
